import { LocaleService } from '@sm360/widget-commons-core-services';
import { isObjectLike, isEmpty, forEach, isNaN } from 'lodash';
import config from 'commons-vehicle/config/index.js';

import * as PurchaseMethodsService from 'commons-vehicle/PurchaseMethods/services/PurchaseMethodsService/PurchaseMethodsService.js';

import VehicleModel from 'commons-vehicle/Vehicle/models/VehicleModel.js';
import MultiMediaModel from 'commons-vehicle/Vehicle/models/MultiMediaModel.js';
import EngineModel from 'commons-vehicle/ModelYear/models/EngineModel.js';
import TransmissionModel from 'commons-vehicle/ModelYear/models/TransmissionModel.js';
import VehicleModelV25 from 'commons-vehicle/Vehicle/models/VehicleModelV25.js';
import InventoryMultimediaModelV25 from 'commons-vehicle/Vehicle/models/InventoryMultimediaModelV25.js';
import { VEHICLE } from 'commons-vehicle/constants/vehicle.js';

const imagesServer = config.source.image;
const imagesBasePath = '/images/newcar';

export function resetVehicleFromWebAutoInventory(inventory) {
    if (!isObjectLike(inventory)) {
        return new VehicleModel();
    }

    let driveTrain = '';
    let engine = '';
    let fuelType = '';
    let fuelCity = 0.0;
    let fuelHighway = 0.0;
    let transmission = '';
    let exteriorColour = '';
    let interiorColour = '';
    let bodyStyle = '';
    let doors = 0;
    let seating = 0;

    if (!isEmpty(inventory.characteristics)) {
        if (!isEmpty(inventory.characteristics.transmission)) {
            transmission = inventory.characteristics.transmission.label;
            if (!isEmpty(inventory.characteristics.transmission.driveTrain)) {
                driveTrain = inventory.characteristics.transmission.driveTrain.label;
            }
        }

        if (!isEmpty(inventory.characteristics.engine)) {
            engine = inventory.characteristics.engine.description;
            fuelType = inventory.characteristics.engine.fuel ? inventory.characteristics.engine.fuel.label : '';
        }

        if (!isEmpty(inventory.characteristics.exteriorColor)) {
            exteriorColour = inventory.characteristics.exteriorColor.label;
        }

        if (!isEmpty(inventory.characteristics.interiorColor)) {
            interiorColour = inventory.characteristics.interiorColor.label;
        }

        if (!isEmpty(inventory.characteristics.body)) {
            doors = inventory.characteristics.body.doors;
            seating = inventory.characteristics.body.passengers;
            bodyStyle = inventory.characteristics.body.frameStyle
                ? inventory.characteristics.body.frameStyle.label
                : '';
        }

        if (!isEmpty(inventory.characteristics.consumptions)) {
            forEach(inventory.characteristics.consumptions, (consumption) => {
                if (consumption.type === 'IN_CITY') {
                    fuelCity = consumption.quantity;
                } else if (consumption.type === 'ON_HIGHWAY') {
                    fuelHighway = consumption.quantity;
                }
            });
        }
    }

    let flags = [];
    let vehicleConditionType = VEHICLE.CONDITIONS.USED;
    if (!isEmpty(inventory.flags)) {
        flags = inventory.flags;

        const isCertified =
            flags.filter((flag) => {
                return flag.type === 'CERTIFIED';
            }).length > 0;

        const isNew =
            flags.filter((flag) => {
                return flag.type === 'NEW';
            }).length > 0;

        if (isCertified) vehicleConditionType = VEHICLE.CONDITIONS.CERTIFIED;
        if (isNew) vehicleConditionType = VEHICLE.CONDITIONS.NEW;
    }

    return new VehicleModel({
        vehicleId: inventory.id,
        showroomVehicleId: inventory.showroomVehicleId || '',
        vehicleConditionType,
        vin: inventory.vin,
        stockNumber: inventory.stockNo || '',
        sold: inventory.sold,
        dateSold: inventory?.dateSold || null,
        year: inventory.year,
        make: inventory.make ? inventory.make.name : '',
        model: inventory.model ? inventory.model.name : '',
        trim: inventory.trim ? inventory.trim.name : '',
        bodyStyle,
        driveTrain,
        engine,
        fuelType,
        flags,
        fuelCity,
        fuelHighway,
        transmission,
        exteriorColour,
        interiorColour,
        doors,
        seating,
        odometer: inventory.odometer,
        sellingPrice: inventory.prices ? inventory.prices.sale : 0,
        discount: inventory.prices ? inventory.prices.regular - inventory.prices.sale : 0,
        features: !isEmpty(inventory.options) ? inventory.options.labels : [],
        multimedia: new MultiMediaModel(inventory.multimedia),
        tagLine: inventory.tagline || '',
        note: inventory.description,
        recentViewCount: inventory.pageViewSummary ? inventory.pageViewSummary.recentViewCount : 0,
        locked: inventory.onHold,
        warranty: inventory.warranty,
        online: inventory.online,
    });
}

// TODO: mapping between WebAuto and VehicleModelV25 is partial, the most important fields are mapped only
export function resetVehicleV25FromWebAutoInventory(inventory) {
    if (!isObjectLike(inventory)) {
        return new VehicleModelV25();
    }

    let driveTrain = '';
    let engine = '';
    let fuelType = '';
    let transmission = '';
    let exteriorColour = '';
    let interiorColour = '';
    let bodyStyle = '';
    let doors = 0;
    let seating = 0;
    let certified = false;
    let newVehicle = false;
    let inspected = false;

    if (!isEmpty(inventory.characteristics)) {
        if (!isEmpty(inventory.characteristics.transmission)) {
            transmission = inventory.characteristics.transmission.label;
            if (!isEmpty(inventory.characteristics.transmission.driveTrain)) {
                driveTrain = inventory.characteristics.transmission.driveTrain.label;
            }
        }

        if (!isEmpty(inventory.characteristics.engine)) {
            engine = inventory.characteristics.engine.description;
            fuelType = inventory.characteristics.engine.fuel ? inventory.characteristics.engine.fuel.label : '';
        }

        if (!isEmpty(inventory.characteristics.exteriorColor)) {
            exteriorColour = inventory.characteristics.exteriorColor.label;
        }

        if (!isEmpty(inventory.characteristics.interiorColor)) {
            interiorColour = inventory.characteristics.interiorColor.label;
        }

        if (!isEmpty(inventory.characteristics.body)) {
            doors = inventory.characteristics.body.doors;
            seating = inventory.characteristics.body.passengers;
            bodyStyle = inventory.characteristics.body.frameStyle
                ? inventory.characteristics.body.frameStyle.label
                : '';
        }
    }

    let flags = [];
    if (!isEmpty(inventory.flags)) {
        flags = inventory.flags;

        const isCertified =
            flags.filter((flag) => {
                return flag.type === 'CERTIFIED';
            }).length > 0;

        const isNew =
            flags.filter((flag) => {
                return flag.type === 'NEW';
            }).length > 0;

        const isInspected =
            flags.filter((flag) => {
                return flag.type === 'NEW';
            }).length > 0;

        if (isCertified) certified = true;
        if (isNew) newVehicle = true;
        if (isInspected) inspected = true;
    }

    return new VehicleModelV25({
        vehicleId: inventory.id,
        showroomVehicleId: inventory.showroomVehicleId || '',
        certified,
        inspected,
        newVehicle,
        vin: inventory.vin,
        stockNumber: inventory.stockNo || '',
        sold: inventory.sold,
        dateSold: inventory?.dateSold || null,
        year: inventory.year,
        make: inventory.make ? inventory.make : {},
        model: inventory.model ? inventory.model : {},
        trim: inventory.trim ? inventory.trim : {},
        bodyStyle,
        driveTrain: {
            name: driveTrain,
        },
        engine,
        fuelType,
        transmission: {
            name: transmission,
        },
        exteriorColour,
        interiorColour,
        doors,
        seating,
        odometer: inventory.odometer,
        sellingPrice: inventory.prices ? inventory.prices.sale : 0,
        discount: inventory.prices ? inventory.prices.regular - inventory.prices.sale : 0,
        features: !isEmpty(inventory.options) ? inventory.options.labels : [],
        multimedia: new InventoryMultimediaModelV25(inventory.multimedia),
        tagLine: inventory.tagline || '',
        note: inventory.description,
        recentViewCount: inventory.pageViewSummary ? inventory.pageViewSummary.recentViewCount : 0,
        locked: inventory.onHold,
        warranty: inventory.warranty,
        online: inventory.online,
    });
}

export function resetVehicleFromShowroomVehicle(vehicle) {
    if (!isObjectLike(vehicle) || isEmpty(vehicle)) {
        return new VehicleModel();
    }

    const {
        id,
        vehicleId,
        catalogVehicleId,
        newCarTrimId,
        vin,
        vehicleCondition,
        year,
        make,
        model,
        bodyColourStatus,
        bodyStyleCode,
        bodyStyle,
        cabSizeCode,
        cabSize,
        boxSizeCode,
        boxSize,
        driveTrainDescription,
        modelTrim,
        modelTrimCode,
        modelTrimStyle,
        bodyColour,
        engineFeatures,
        transmissionFeatures,
        configurationID,
        configurationToken,
        odometer,
        inventory,
        sold,
    } = vehicle;

    let engine = '';
    if (engineFeatures) {
        const engineModel = new EngineModel({
            fuelTypeCode: engineFeatures.fuelType,
            cylinders: engineFeatures.cylinders,
            displacement: engineFeatures.displacement,
            isTurbo: engineFeatures.isTurbo,
            hp: engineFeatures.hp,
            hpRpm: engineFeatures.hpRpm,
            torque: engineFeatures.torque,
            torqueRpm: engineFeatures.torqueRpm,
        });
        engine = engineModel.getSizeSpecifications(LocaleService);
    }

    let transmission = '';
    if (transmissionFeatures) {
        const transmissionModel = new TransmissionModel({
            typeCode: transmissionFeatures.transmissionTypeCode,
            type: transmissionFeatures.transmissionType,
            speeds: transmissionFeatures.speeds,
            isCVT: transmissionFeatures.isCVT,
            description: transmissionFeatures.transmissionTypeDescription[0],
        });
        transmission = transmissionModel.getTransmission(LocaleService);
    }

    let multimedia = new MultiMediaModel();
    if (!isEmpty(bodyColour)) {
        const url = `${imagesServer}${imagesBasePath}/${bodyColour.vehiclePhotoUrl}`;
        const url360x270 = `${imagesServer}/ir/w360h270${imagesBasePath}/${bodyColour.vehiclePhotoUrl}`;
        const url360x240 = `${imagesServer}/ir/w360h197c${imagesBasePath}/${bodyColour.vehiclePhotoUrl}`;
        const url440 = `${imagesServer}/ir/w440${imagesBasePath}/${bodyColour.vehiclePhotoUrl}`;

        multimedia = multimedia.set('mainPicture', { url, url360x270 }).set('catalog', {
            side: {
                url,
                url360x240,
                url360x270,
                url440,
            },
        });
    }

    const vehicleIdInt = parseInt(vehicleId, 10);
    const catalogVehicleIdInt = parseInt(catalogVehicleId, 10);
    const newCarTrimIdInt = parseInt(newCarTrimId, 10);
    return new VehicleModel({
        id,
        vehicleId: isNaN(vehicleIdInt) ? 0 : vehicleIdInt,
        catalogVehicleId: isNaN(catalogVehicleIdInt) ? 0 : catalogVehicleIdInt,
        newCarTrimId: isNaN(newCarTrimIdInt) ? 0 : newCarTrimIdInt,
        vehicleConditionType: vehicleCondition,
        vin,
        stockNumber: inventory ? inventory.stock : '',
        year,
        make,
        model,
        modelTrim,
        modelTrimStyle,
        bodyColour,
        exteriorColourStatus: bodyColourStatus,
        bodyStyle,
        bodyStyleCode,
        cabSizeCode,
        cabSize,
        boxSizeCode,
        boxSize,
        driveTrain: driveTrainDescription,
        modelTrimCode,
        engine,
        transmission,
        odometer,
        multimedia,
        configurationToken,
        configurationId: configurationID,
        sold,
    });
}

export function resetVehicleFromShowroomQuoteVehicle(quote) {
    if (!isObjectLike(quote) || !quote.vehicle || !isObjectLike(quote.vehicle)) {
        return new VehicleModel();
    }

    return resetVehicleFromShowroomVehicle(quote.vehicle);
}

export function setVehiclePurchaseMethods(vehicle, purchaseMethods) {
    if (!isObjectLike(purchaseMethods)) {
        return vehicle.set(
            'purchaseMethods',
            PurchaseMethodsService.selectPurchaseMethod(vehicle.purchaseMethods, purchaseMethods)
        );
    }
    return vehicle.set('purchaseMethods', purchaseMethods);
}

export function setBestPaymentTerm(vehicle, maximumTerm) {
    const selectedFinanceTerm = vehicle.purchaseMethods.getSelectedFinanceTerm();
    if (!selectedFinanceTerm) {
        return vehicle;
    }

    return vehicle.set(
        'purchaseMethods',
        PurchaseMethodsService.setSelectedFinanceBestPaymentTerm(vehicle.purchaseMethods, maximumTerm)
    );
}

export function selectPaymentFrequency(vehicle, paymentFrequency) {
    return vehicle.set(
        'purchaseMethods',
        PurchaseMethodsService.selectPaymentFrequency(vehicle.purchaseMethods, paymentFrequency)
    );
}

export function setCashDown(vehicle, cashDown) {
    return vehicle.set('purchaseMethods', PurchaseMethodsService.setCashDown(vehicle.purchaseMethods, cashDown));
}

export function setTradeIn(vehicle, tradeIn) {
    return vehicle.set('purchaseMethods', PurchaseMethodsService.setTradeIn(vehicle.purchaseMethods, tradeIn));
}

export function setLien(vehicle, lien) {
    return vehicle.set('purchaseMethods', PurchaseMethodsService.setLien(vehicle.purchaseMethods, lien));
}

export function setTargetPayment(vehicle, targetPayment) {
    const targetCashDown = vehicle.purchaseMethods.getCashDownFromTargetPayment(targetPayment);
    return vehicle.set('purchaseMethods', PurchaseMethodsService.setCashDown(vehicle.purchaseMethods, targetCashDown));
}

export function isVehicleNew(vehicle) {
    if (vehicle && vehicle.vehicleConditionType) {
        if (
            vehicle.vehicleConditionType === VEHICLE.CONDITIONS.NEW ||
            vehicle.vehicleConditionType === VEHICLE.CONDITIONS.DEMO
        ) {
            return true;
        }
    }
    return false;
}

export default {
    resetVehicleFromWebAutoInventory,
    resetVehicleV25FromWebAutoInventory,
    resetVehicleFromShowroomQuoteVehicle,
    resetVehicleFromShowroomVehicle,
    setVehiclePurchaseMethods,
    setBestPaymentTerm,
    selectPaymentFrequency,
    setCashDown,
    setTradeIn,
    setLien,
    setTargetPayment,
    isVehicleNew,
};
